/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { DefaultAngularLand } from 'src/app/common/fancy-firebase/util/default-angular-land.service';
import { FirebaseListFactory } from 'src/app/common/fancy-firebase/services/factories';
import { FancyFragmenter } from 'src/app/common/fancy-firebase/base/FancyFragmenter';
import { FancyAction } from 'src/app/common/fancy-firebase/base/FancyAction';
import { FancyFirebaseList } from 'src/app/common/fancy-firebase/base/FancyFirebaseList';
import { FancyIdJoin } from 'src/app/common/fancy-firebase/base/FancyIdJoin';
import {
	fancyFirebaseGetters,
	fancyFirebaseReferencer,
	ProjectCatalogType,
} from '@touchplan/fancy-referencer';
import { OrganizationProjectsApi } from 'src/app/app-data/state/OrganizationProjectsApi';
import { OrganizationProjectList } from 'src/app/common/fancy-firebase/lists/OrganizationProjectList';
import { FancyFirebaseState } from 'src/app/common/fancy-firebase/services/fancy-firebase-state.service';
import { EMPTY } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ILoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';

import firebase from 'firebase/compat/app';

@Injectable({
	providedIn: 'root',
})
export class FancyFirebaseOrganizationProjectsApiBuilder {
	private logger: ILoggerService = LoggerServiceFactory.create('FancyFirebaseOrganizationProjectsApiBuilder');

	constructor(
		private defaultAngularLand: DefaultAngularLand,
		private firebaseListFactory: FirebaseListFactory,
		private fancyFirebaseState: FancyFirebaseState
	) {}

	public getOrganizationProjectsApi(organizationId: string) {
		const refGetter = (projectId: string) => {
			return this.fancyFirebaseState.getProjectCatalog$(projectId, ProjectCatalogType.ProjectData).pipe(
				catchError((err) => {
					this.logger.error(err);
					return EMPTY;
				}),
				//add a distinct until changed here against the partition name?
				map(() =>
					fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
						fancyFirebaseGetters.getProjectsPath(projectId),
						projectId
					)
				)
			);
		};

		const projectListFragment = new FancyFragmenter(this.defaultAngularLand, refGetter);
		const projectList = new FancyFirebaseList(
			this.defaultAngularLand,
			projectListFragment.emitter$,
			FancyAction
		);
		projectListFragment.start();
		const projectsJoiner = new FancyIdJoin('projectId', projectList, projectListFragment);

		const organizationProjectListPath = fancyFirebaseGetters.getOrganizationProjectsPath(organizationId);
		const organizationProjectList = this.firebaseListFactory.create(
			OrganizationProjectList,
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(organizationProjectListPath),
			[projectsJoiner]
		);

		const organizationProjectsApi = new OrganizationProjectsApi(organizationProjectList);

		organizationProjectsApi.addDestroyer(
			() => projectsJoiner.destroy(),
			() => projectListFragment.destroy(),
			() => projectList.destroy(),
			() => projectsJoiner.destroy()
		);

		return organizationProjectsApi;
	}
}
